import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { MenuItem, SxProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useModal } from '@europrocurement/flexy-components';
import { ApiDossiersIdActionPutActionEnum } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useNavigate } from 'react-router-dom';
import SelectSociete from '@europrocurement/l2d-modules/modules/ParcoursFormalite/components/SelectSociete';
import useTransition from './useTransition';
import { DossierAction } from '../providers/DossierActionContext';
import DossierFormValidationStatusModal from '../components/detail/DossierFormValidationStatusModal/DossierFormValidationStatusModal';
import { useDossierCustomHeader } from '../providers/DossierCustomHeaderContext';
import { useDossierRoutingContext } from '../providers/DossierRoutingContext';
import useDossierClient from './useDossierClient';

type useDossierActionMenuItemsProps = {
    dossier?: DossierOpenSearch;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    triggerAction: (action: DossierAction, selectedDossier: Array<DossierOpenSearch>) => void;
    refreshDossier: () => void;
    getOrderDuplicateRoute?: (id: number) => string;
    callAdditionnalCollectionRequestModal?: () => Promise<void>;
    siblings?: DossierOpenSearch[];
};

const useDossierActionMenuItemsBack = ({
    dossier,
    triggerAction,
    setAnchorEl,
    refreshDossier,
    getOrderDuplicateRoute,
    callAdditionnalCollectionRequestModal,
    siblings,
}: useDossierActionMenuItemsProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { canApplyTransition, applyTransition } = useTransition();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const navigate = useNavigate();

    const { setHeaderButton, setHeaderTitle, setPathOnExit } = useDossierCustomHeader();
    const { getDossierValidationRoute } = useDossierRoutingContext();
    const { updateDossierClientOpere } = useDossierClient();

    const isDossierLoaded = typeof dossier !== 'undefined';

    const onlyIfDossierLoad = (element?: React.JSX.Element) =>
        isDossierLoaded ? element : undefined;

    const canSuspend = isDossierLoaded && canApplyTransition(dossier, 'suspend');
    const canUnSuspend = isDossierLoaded && canApplyTransition(dossier, 'unsuspend');
    const canUpdateSocieteOperee = isDossierLoaded && dossier.numero?.startsWith('LDF');
    const canAssign = true;

    const handleOpenModalDossierAction = (action: DossierAction) => {
        if (!dossier) return;
        triggerAction(action, [dossier]);
        setAnchorEl(null);
    };

    const handleSubmitFormStatus = async (
        actionIdentifier: ApiDossiersIdActionPutActionEnum | 'save',
    ) => {
        if (!dossier) return;

        if (actionIdentifier === 'save') {
            modalActions.close();
        } else if (canApplyTransition(dossier, actionIdentifier)) {
            applyTransition(dossier, actionIdentifier)
                .then(() => {
                    modalActions.close();
                    refreshDossier();
                })
                .catch((err) => {
                    modalActions.close();
                    apiErrorMessageSnackbar(
                        err,
                        'Une erreur est survenue lors de la modification du dossier',
                    );
                });
        }
    };

    const handleUpdateDossierStatut = () => {
        if (isDossierLoaded) {
            modalActions.call(
                <DossierFormValidationStatusModal
                    dossier={dossier}
                    withoutSaveForm
                    handleSubmit={handleSubmitFormStatus}
                />,
            );
        }
    };

    const handleUpdateSocieteOperee = () => {
        if (isDossierLoaded && siblings) {
            modalActions.call(
                <SelectSociete
                    title="Modifier la société opérée"
                    description=""
                    multiple={false}
                    societeSelectedDefault={[dossier.clientOpereId!.toString()]}
                    onSelect={async (ids) => {
                        if (ids.length === 1) {
                            try {
                                const updateClientOperePromises = siblings.map((doss) =>
                                    updateDossierClientOpere(doss.id as number, ids[0]),
                                );

                                Promise.all(updateClientOperePromises)
                                    .then(() => {
                                        refreshDossier();
                                        enqueueSnackbar('La société opérée a bien été modifiée');
                                        modalActions.close();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                        enqueueSnackbar(
                                            'Une erreur est survenue lors de la modification de la société opérée',
                                            {
                                                variant: 'error',
                                            },
                                        );
                                    });
                            } catch (error) {
                                console.log(error);
                                enqueueSnackbar(
                                    'Une erreur est survenue lors de la modification de la société opérée',
                                    {
                                        variant: 'error',
                                    },
                                );
                            }
                        } else {
                            enqueueSnackbar('Une seule société est attendue', { variant: 'error' });
                        }
                    }}
                    idFormalites={dossier.formalites.map((formality) => formality.id as number)}
                />,
            );
        }
    };

    const navigateToInfoValidation = () => {
        if (!isDossierLoaded || !dossier) return;

        const currentLocation = window.location.pathname + window.location.search;

        setHeaderTitle(`Vérifier les informations du dossier ${dossier.numero}`);
        setHeaderButton('Enregister et quitter');
        setPathOnExit(currentLocation);

        const path = getDossierValidationRoute(dossier.id as number);
        if (path) navigate(path);
    };

    type renderMenuItemProps = {
        key: string;
        action: () => void;
        iconName?: IconName;
        label?: string;
        textSx?: SxProps;
        iconSx?: SxProps;
    };

    const renderMenuItem = ({
        key,
        action,
        iconName = 'exclamation-circle',
        label = 'menu item',
        textSx,
        iconSx,
    }: renderMenuItemProps) => (
        <MenuItem
            key={key}
            onClick={action}
            sx={{ gap: '4px' }}
        >
            <FaOptionIcon
                icon={['fasl', iconName]}
                color="text.gris70"
                sx={iconSx}
            />
            <Typography sx={{ color: 'text.primary', ...textSx }}>{label}</Typography>
        </MenuItem>
    );

    const groupDossierMenuItem = renderMenuItem({
        key: 'groupDossierActionItem',
        action: () => handleOpenModalDossierAction('manageGroups'),
        iconName: 'object-group',
        label: 'Déplacer dans un groupe',
    });

    const exportDossierMenuItem = renderMenuItem({
        key: 'exportActionItem',
        action: () => handleOpenModalDossierAction('export'),
        iconName: 'paper-plane',
        label: 'Envoyer un récapitulatif',
    });

    const returnUrgentDossierMenuItem = () =>
        dossier?.critical === false
            ? renderMenuItem({
                  key: 'urgentActionItem',
                  action: () => handleOpenModalDossierAction('urgent'),
                  iconName: 'exclamation-circle',
                  label: 'Marquer comme urgent',
              })
            : undefined;

    const duplicateDossierMenuItem = renderMenuItem({
        key: 'duplicatedActionItem',
        action: () => {
            if (getOrderDuplicateRoute && dossier?.collectionId) {
                navigate(getOrderDuplicateRoute(dossier.collectionId));
            }
        },
        iconName: 'clone',
        label: 'Dupliquer la commande',
    });

    const updateDossierStatus = renderMenuItem({
        key: 'statusDossierItem',
        action: handleUpdateDossierStatut,
        iconName: 'arrow-progress',
        label: 'Modifier le statut du dossier',
    });

    const checkInfos = renderMenuItem({
        key: 'checkInfosItem',
        action: navigateToInfoValidation,
        iconName: 'badge-check',
        label: 'Vérifier les informations',
    });

    const returnAdditionnalClientRequest = () =>
        typeof callAdditionnalCollectionRequestModal !== 'undefined'
            ? renderMenuItem({
                  key: 'additionnalRequestItem',
                  action: () => callAdditionnalCollectionRequestModal(),
                  iconName: 'paper-plane',
                  label: 'Demande complémentaire',
              })
            : undefined;

    const returnAssignUserDossierMenuItem = () =>
        canAssign
            ? renderMenuItem({
                  key: 'assignUserActionItem',
                  action: () => handleOpenModalDossierAction('assign'),
                  iconName: 'user',
                  label: 'Assigner à un formaliste',
              })
            : undefined;

    const returnSuspendDossierMenuItem = () =>
        canSuspend
            ? renderMenuItem({
                  key: 'suspendActionItem',
                  action: () => handleOpenModalDossierAction('suspend'),
                  iconName: 'pause',
                  label: 'Suspendre le dossier',
              })
            : undefined;

    const returnUnSuspendDossierMenuItem = () =>
        canUnSuspend
            ? renderMenuItem({
                  key: 'unsuspendActionItem',
                  action: () => handleOpenModalDossierAction('unsuspend'),
                  iconName: 'backward-step',
                  label: 'Reprendre le dossier',
              })
            : undefined;

    const returnUpdateDossierSocieteOpereeItem = () =>
        canUpdateSocieteOperee
            ? renderMenuItem({
                  key: 'updateSocieteOpereeItem',
                  action: () => handleUpdateSocieteOperee(),
                  iconName: 'building-user',
                  label: 'Modifier la société opérée',
              })
            : undefined;

    const dossierActionMenuItemsBack = [
        groupDossierMenuItem,
        exportDossierMenuItem,
        returnAssignUserDossierMenuItem(),
        returnUrgentDossierMenuItem(),
        returnSuspendDossierMenuItem(),
        returnUnSuspendDossierMenuItem(),
    ].filter(Boolean) as React.JSX.Element[];

    const dossierDetatilQuickActionBack = [
        onlyIfDossierLoad(updateDossierStatus),
        onlyIfDossierLoad(checkInfos),
        returnAdditionnalClientRequest(),
        exportDossierMenuItem,
        groupDossierMenuItem,
        returnSuspendDossierMenuItem(),
        returnUnSuspendDossierMenuItem(),
        returnUrgentDossierMenuItem(),
        onlyIfDossierLoad(duplicateDossierMenuItem),
        returnUpdateDossierSocieteOpereeItem(),
    ].filter(Boolean) as React.JSX.Element[];

    return {
        dossierActionMenuItemsBack,
        dossierDetatilQuickActionBack,
    };
};

export default useDossierActionMenuItemsBack;
